import React, { useEffect, useState } from "react";
import {
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Typography,
	Grid,
	CircularProgress,
	Box,
	Card,
	Chip,
	Avatar,
	Paper,
	IconButton,
	Tooltip,
	Fade,
	Zoom,
	TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
	FaCheckCircle,
	FaFileAlt,
	FaCalendarAlt,
	FaHashtag,
	FaDatabase,
	FaUsers,
	FaTimes,
	FaSpinner,
	FaTimesCircle,
} from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";

const ApproveFile = () => {
	const [files, setFiles] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedFile, setSelectedFile] = useState(null);
	const [open, setOpen] = useState(false);
	const [approving, setApproving] = useState(false);
	const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
	const [rejecting, setRejecting] = useState(false);
	const [rejectComment, setRejectComment] = useState("");

	const fetchFiles = async () => {
		setLoading(true);
		try {
			const response = await axios.get(
				"https://bulksms.approot.ng/uploaded_files.php"
			);
			if (response.data.status) {
				setFiles(response.data.files);
			} else {
				toast.error(response.data.message || "Failed to fetch files");
			}
		} catch (err) {
			toast.error("Error fetching files");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchFiles();
	}, []);

	const handleOpen = (file) => {
		setSelectedFile(file);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedFile(null);
		setRejectComment("");
	};

	const handleRejectDialogOpen = () => {
		setRejectDialogOpen(true);
	};

	const handleRejectDialogClose = () => {
		setRejectDialogOpen(false);
		setRejectComment("");
	};

	// Updated handleApprove - only sends the ID
	const handleApprove = async () => {
		if (!selectedFile) return;
		setApproving(true);
		try {
			const response = await axios.post(
				"https://bulksms.approot.ng/approved.php",
				{
					id: selectedFile.id,
				},
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);

			if (response.data.status) {
				toast.success("✅ File approved successfully");
				fetchFiles();
				setFiles((prev) =>
					prev.map((f) =>
						f.id === selectedFile.id
							? {
									...f,
									status: "approved",
									approved_by: response.data.approved_by,
							  }
							: f
					)
				);
				handleClose();
			} else {
				toast.error(response.data.message || "Approval failed");
			}
		} catch (err) {
			toast.error("Error approving file");
		} finally {
			setApproving(false);
		}
	};

	// Handle reject function
	const handleReject = async () => {
		if (!selectedFile || !rejectComment.trim()) {
			toast.error("Please provide a reason for rejection");
			return;
		}
		setRejecting(true);
		try {
			const response = await axios.post(
				"https://bulksms.approot.ng/reject.php",
				{
					id: selectedFile.id,
					comment: rejectComment.trim(),
				},
				{
					headers: {
						Authorization: `Bearer ${localStorage.getItem("token")}`,
					},
				}
			);

			if (response.data.status) {
				toast.success("❌ File rejected successfully");
				fetchFiles();
				setFiles((prev) =>
					prev.map((f) =>
						f.id === selectedFile.id
							? {
									...f,
									status: "rejected",
									rejected_by: response.data.rejected_by,
									rejection_comment: rejectComment.trim(),
							  }
							: f
					)
				);
				handleClose();
				handleRejectDialogClose();
			} else {
				toast.error(response.data.message || "Rejection failed");
			}
		} catch (err) {
			toast.error("Error rejecting file");
		} finally {
			setRejecting(false);
		}
	};

	const getStatusColor = (status) => {
		switch (status?.toLowerCase()) {
			case "approved":
				return "success";
			case "6" || "pending" || "queued":
				return "warning";
			case "rejected":
				return "error";
			default:
				return "default";
		}
	};

	const getStatusIcon = (status) => {
		switch (status?.toLowerCase()) {
			case "6":
				return <FaCheckCircle />;
			case "pending":
				return <FaSpinner />;
			default:
				return <FaFileAlt />;
		}
	};

	const getMessageUnits = (message) => {
		const specialChars = ["€", "^", "{", "}", "[", "]", "~", "|"];
		let count = 0;
		for (let i = 0; i < message.length; i++) {
			count += specialChars.includes(message[i]) ? 2 : 1;
		}
		return count;
	};

	// helper: compute amount
	const computeAmount = (message, totalCount) => {
		if (!message) return 0;
		const charUnits = getMessageUnits(message);
		const pages = charUnits <= 160 ? 1 : Math.ceil(charUnits / 153);
		return 5 * pages * (parseInt(totalCount, 10) || 0);
	};

	// Only calculate when a file is selected
	const charUnits = selectedFile?.message
		? getMessageUnits(selectedFile.message)
		: 0;

	const adjustedCostTo = selectedFile
		? computeAmount(selectedFile.message, selectedFile.total_count)
		: 0;

	// Enhanced DataGrid Columns with bank theme colors
	const columns = [
		{
			field: "id",
			headerName: "ID",
			width: 140,
		},
		{
			field: "actions",
			headerName: "Action",
			width: 140,
			renderCell: (params) => (
				<Zoom in={true} timeout={300}>
					<Tooltip title='Approve this file' arrow placement='top'>
						<Button
							startIcon={<FaCheckCircle />}
							variant='contained'
							size='small'
							onClick={() => handleOpen(params.row)}
							disabled={params.row.status === "approved"}
							sx={{
								backgroundColor: "#dc2626", // Red
								color: "white",
								borderRadius: 2,
								textTransform: "none",
								fontWeight: 600,
								boxShadow: "0 2px 8px rgba(220, 38, 38, 0.3)",
								"&:hover": {
									backgroundColor: "#b91c1c", // Darker red
									boxShadow: "0 4px 16px rgba(220, 38, 38, 0.4)",
									transform: "translateY(-1px)",
								},
								"&:disabled": {
									backgroundColor: "#e5e7eb",
									color: "#9ca3af",
								},
								transition: "all 0.2s ease-in-out",
							}}
						>
							Approve
						</Button>
					</Tooltip>
				</Zoom>
			),
		},
		{
			field: "message",
			headerName: "Amount (₦)",
			width: 160,
			renderCell: (params) => {
				console.log(params.value, params.row);
				const amount = computeAmount(params.value, params.row.total_count);
				return (
					<Typography variant='body2' fontWeight={600} color='#dc2626'>
						₦{amount.toLocaleString()}
					</Typography>
				);
			},
		},
		{
			field: "batch_id",
			headerName: "Batch ID",
			width: 180,
			renderCell: (params) => (
				<Box display='flex' alignItems='center' gap={1}>
					<FaHashtag color='#666' size={12} />
					<Typography variant='body2' fontWeight={500}>
						{params.value}
					</Typography>
				</Box>
			),
		},
		{
			field: "full_name",
			headerName: "Uploaded By",
			width: 200,
			renderCell: (params) => (
				<Box display='flex' alignItems='center' gap={1}>
					<Avatar
						sx={{
							width: 28,
							height: 28,
							bgcolor: "#dc2626", // Red
							fontSize: "0.75rem",
						}}
					>
						{params.value?.charAt(0)?.toUpperCase() || "U"}
					</Avatar>
					<Typography variant='body2' fontWeight={500}>
						{params.value || "Unknown"}
					</Typography>
				</Box>
			),
		},
		{
			field: "total_count",
			headerName: "Total Count",
			width: 140,
			renderCell: (params) => (
				<Box display='flex' alignItems='center' gap={1}>
					<FaDatabase color='#dc2626' size={12} />
					<Typography
						variant='body2'
						fontWeight={600}
						sx={{ color: "#dc2626" }}
					>
						{params.value?.toLocaleString() || 0}
					</Typography>
				</Box>
			),
		},
		{
			field: "total_distinct",
			headerName: "Unique Numbers",
			width: 160,
			renderCell: (params) => (
				<Box display='flex' alignItems='center' gap={1}>
					<FaUsers color='#dc2626' size={12} />
					<Typography
						variant='body2'
						fontWeight={600}
						sx={{ color: "#dc2626" }}
					>
						{params.value?.toLocaleString() || 0}
					</Typography>
				</Box>
			),
		},
		{
			field: "created_at",
			headerName: "Uploaded At",
			width: 180,
			renderCell: (params) => (
				<Box display='flex' alignItems='center' gap={1}>
					<FaCalendarAlt color='#9ca3af' size={12} />
					<Typography variant='body2'>
						{new Date(params.value).toLocaleDateString("en-US", {
							month: "short",
							day: "numeric",
							hour: "2-digit",
							minute: "2-digit",
						})}
					</Typography>
				</Box>
			),
		},
		{
			field: "status",
			headerName: "Status",
			width: 140,
			renderCell: (params) => (
				<Chip
					icon={getStatusIcon(params.value)}
					label={"Queued"}
					color={getStatusColor(params.value)}
					variant='outlined'
					size='small'
					sx={{
						fontWeight: 600,
						textTransform: "capitalize",
						borderRadius: 2,
					}}
				/>
			),
		},
	];

	return (
		<Box
			sx={{
				background:
					"linear-gradient(135deg, #dc2626 0%, #b91c1c 50%, #991b1b 100%)", // Red gradient
				minHeight: "100vh",
				p: 4,
			}}
		>
			<Paper
				elevation={24}
				sx={{
					borderRadius: 4,
					overflow: "hidden",
					background: "rgba(255, 255, 255, 0.98)",
					backdropFilter: "blur(20px)",
					border: "1px solid rgba(220, 38, 38, 0.1)",
				}}
			>
				<Box
					sx={{
						background: "linear-gradient(45deg, #dc2626 30%, #b91c1c 90%)", // Red gradient
						color: "white",
						p: 4,
						textAlign: "center",
					}}
				>
					<Typography
						variant='h4'
						fontWeight='bold'
						sx={{
							mb: 1,
							textShadow: "0 2px 4px rgba(0,0,0,0.3)",
						}}
					>
						🏦 SMS Approval Center
					</Typography>
					<Typography variant='subtitle1' sx={{ opacity: 0.9 }}>
						Review and approve uploaded SMS for bulk SMS processing
					</Typography>
				</Box>

				<Box p={4}>
					{loading ? (
						<Fade in={loading}>
							<Box
								display='flex'
								flexDirection='column'
								justifyContent='center'
								alignItems='center'
								py={8}
							>
								<CircularProgress
									size={60}
									thickness={4}
									sx={{ color: "#dc2626" }}
								/>
								<Typography
									variant='h6'
									sx={{ mt: 3, color: "text.secondary" }}
								>
									Loading SMS details...
								</Typography>
							</Box>
						</Fade>
					) : (
						<Fade in={!loading}>
							<Card
								elevation={8}
								sx={{
									borderRadius: 3,
									overflow: "hidden",
									border: "1px solid rgba(220, 38, 38, 0.08)",
								}}
							>
								<DataGrid
									rows={files}
									columns={columns}
									getRowId={(row) =>
										row.id || Math.random().toString(36).substr(2, 9)
									}
									pageSize={10}
									rowsPerPageOptions={[10, 25, 50]}
									disableSelectionOnClick
									sx={{
										border: "none",
										"& .MuiDataGrid-cell": {
											borderBottom: "1px solid rgba(0,0,0,0.05)",
										},
										"& .MuiDataGrid-columnHeaders": {
											backgroundColor: "#fef2f2", // Light red background
											borderBottom: "2px solid rgba(220, 38, 38, 0.1)",
											fontWeight: 600,
											color: "#991b1b",
										},
										"& .MuiDataGrid-row": {
											"&:hover": {
												backgroundColor: "rgba(220, 38, 38, 0.04)",
											},
										},
									}}
									autoHeight
								/>
							</Card>
						</Fade>
					)}
				</Box>
			</Paper>

			{/* Main Details Dialog */}
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth='md'
				fullWidth
				TransitionComponent={Fade}
				PaperProps={{
					elevation: 24,
					sx: {
						borderRadius: 4,
						background: "linear-gradient(135deg, #fff 0%, #fef2f2 100%)", // White to light red
						border: "1px solid rgba(220, 38, 38, 0.1)",
					},
				}}
			>
				<DialogTitle
					sx={{
						background: "linear-gradient(45deg, #dc2626 30%, #b91c1c 90%)", // Red gradient
						color: "white",
						textAlign: "center",
						position: "relative",
					}}
				>
					<Typography variant='h5' fontWeight='bold'>
						📑 SMS Details
					</Typography>
					<IconButton
						onClick={handleClose}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: "white",
							"&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
						}}
					>
						<FaTimes />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers sx={{ p: 0 }}>
					{selectedFile ? (
						<Box p={3}>
							<Grid container spacing={3}>
								<Grid item xs={12} sm={6}>
									<Card
										elevation={2}
										sx={{
											p: 2,
											borderRadius: 2,
											border: "1px solid rgba(220, 38, 38, 0.08)",
											backgroundColor: "#fef2f2",
										}}
									>
										<Box display='flex' alignItems='center' gap={1} mb={1}>
											<Box color='#dc2626'>
												<FaHashtag />
											</Box>
											<Typography
												variant='subtitle2'
												color='textSecondary'
												fontWeight={600}
												textTransform='capitalize'
											>
												Adjusted Cost
											</Typography>
										</Box>
										<Typography
											variant='body1'
											fontWeight={500}
											color='#dc2626'
										>
											₦{adjustedCostTo.toLocaleString()}
										</Typography>
									</Card>
								</Grid>
								{Object.entries(selectedFile).map(([key, value]) => {
									const getIcon = (key) => {
										switch (key.toLowerCase()) {
											case "batch_id":
												return <FaHashtag />;
											case "total_count":
												return <FaDatabase />;
											case "total_distinct":
												return <FaUsers />;
											case "created_at":
												return <FaCalendarAlt />;
											case "status":
												return getStatusIcon(value);
											default:
												return <FaFileAlt />;
										}
									};

									return (
										<Grid item xs={12} sm={6} key={key}>
											<Card
												elevation={2}
												sx={{
													p: 2,
													borderRadius: 2,
													border: "1px solid rgba(220, 38, 38, 0.08)",
													backgroundColor: "white",
													"&:hover": {
														boxShadow: "0 4px 12px rgba(220, 38, 38, 0.15)",
														backgroundColor: "#fef2f2",
													},
													transition: "all 0.2s ease-in-out",
												}}
											>
												<Box display='flex' alignItems='center' gap={1} mb={1}>
													<Box color='#dc2626'>{getIcon(key)}</Box>
													<Typography
														variant='subtitle2'
														color='textSecondary'
														fontWeight={600}
														textTransform='capitalize'
													>
														{key.replace(/_/g, " ")}
													</Typography>
												</Box>
												<Typography variant='body1' fontWeight={500}>
													{key === "status" ? (
														<Chip
															icon={getStatusIcon(value)}
															label={"Queued"}
															color={getStatusColor(value)}
															size='small'
															sx={{ fontWeight: 600 }}
														/>
													) : (
														value || "—"
													)}
												</Typography>
											</Card>
										</Grid>
									);
								})}
							</Grid>
						</Box>
					) : (
						<Typography p={3} textAlign='center' color='textSecondary'>
							No file selected
						</Typography>
					)}
				</DialogContent>

				<DialogActions
					sx={{
						p: 3,
						background: "linear-gradient(to right, #fef2f2, #fff)",
					}}
				>
					<Button
						onClick={handleClose}
						color='inherit'
						variant='outlined'
						sx={{
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							px: 3,
							borderColor: "#dc2626",
							color: "#dc2626",
							"&:hover": {
								borderColor: "#b91c1c",
								backgroundColor: "rgba(220, 38, 38, 0.04)",
							},
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleRejectDialogOpen}
						variant='outlined'
						disabled={approving}
						startIcon={<FaTimesCircle />}
						sx={{
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							px: 3,
							borderColor: "#ef4444",
							color: "#ef4444",
							"&:hover": {
								borderColor: "#dc2626",
								backgroundColor: "rgba(239, 68, 68, 0.04)",
							},
							"&:disabled": {
								borderColor: "#e5e7eb",
								color: "#9ca3af",
							},
						}}
					>
						Reject SMS
					</Button>
					<Button
						onClick={handleApprove}
						variant='contained'
						disabled={approving}
						startIcon={approving ? <FaSpinner /> : <FaCheckCircle />}
						sx={{
							backgroundColor: "#dc2626",
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							px: 3,
							boxShadow: "0 4px 12px rgba(220, 38, 38, 0.3)",
							"&:hover": {
								backgroundColor: "#b91c1c",
								boxShadow: "0 6px 20px rgba(220, 38, 38, 0.4)",
							},
							"&:disabled": {
								backgroundColor: "#e5e7eb",
								color: "#9ca3af",
							},
						}}
					>
						{approving ? "Approving..." : "Approve SMS"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Reject Comment Dialog */}
			<Dialog
				open={rejectDialogOpen}
				onClose={handleRejectDialogClose}
				maxWidth='sm'
				fullWidth
				TransitionComponent={Fade}
				PaperProps={{
					elevation: 24,
					sx: {
						borderRadius: 4,
						background: "linear-gradient(135deg, #fff 0%, #fef2f2 100%)",
						border: "1px solid rgba(220, 38, 38, 0.1)",
					},
				}}
			>
				<DialogTitle
					sx={{
						background: "linear-gradient(45deg, #ef4444 30%, #dc2626 90%)",
						color: "white",
						textAlign: "center",
						position: "relative",
					}}
				>
					<Typography variant='h5' fontWeight='bold'>
						❌ Reject SMS
					</Typography>
					<IconButton
						onClick={handleRejectDialogClose}
						sx={{
							position: "absolute",
							right: 8,
							top: 8,
							color: "white",
							"&:hover": { backgroundColor: "rgba(255,255,255,0.1)" },
						}}
					>
						<FaTimes />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ p: 3 }}>
					<Typography variant='body1' sx={{ mb: 2, color: "text.secondary" }}>
						Please provide a reason for rejecting this SMS file:
					</Typography>
					<TextField
						fullWidth
						multiline
						rows={4}
						value={rejectComment}
						onChange={(e) => setRejectComment(e.target.value)}
						placeholder='Enter your reason for rejection...'
						variant='outlined'
						sx={{
							"& .MuiOutlinedInput-root": {
								borderRadius: 2,
								"&:hover .MuiOutlinedInput-notchedOutline": {
									borderColor: "#dc2626",
								},
								"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
									borderColor: "#dc2626",
								},
							},
							"& .MuiInputLabel-root.Mui-focused": {
								color: "#dc2626",
							},
						}}
					/>
				</DialogContent>

				<DialogActions
					sx={{ p: 3, background: "linear-gradient(to right, #fef2f2, #fff)" }}
				>
					<Button
						onClick={handleRejectDialogClose}
						color='inherit'
						variant='outlined'
						sx={{
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							px: 3,
							borderColor: "#6b7280",
							color: "#6b7280",
							"&:hover": {
								borderColor: "#4b5563",
								backgroundColor: "rgba(107, 114, 128, 0.04)",
							},
						}}
					>
						Cancel
					</Button>
					<Button
						onClick={handleReject}
						variant='contained'
						disabled={rejecting || !rejectComment.trim()}
						startIcon={rejecting ? <FaSpinner /> : <FaTimesCircle />}
						sx={{
							backgroundColor: "#ef4444",
							borderRadius: 2,
							textTransform: "none",
							fontWeight: 600,
							px: 3,
							boxShadow: "0 4px 12px rgba(239, 68, 68, 0.3)",
							"&:hover": {
								backgroundColor: "#dc2626",
								boxShadow: "0 6px 20px rgba(239, 68, 68, 0.4)",
							},
							"&:disabled": {
								backgroundColor: "#e5e7eb",
								color: "#9ca3af",
							},
						}}
					>
						{rejecting ? "Rejecting..." : "Confirm Rejection"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default ApproveFile;
